'use client';

import { type RefObject, useCallback, useEffect, useState } from 'react';

/**
 * Tracks scroll state of a scrollable element and exposes a programmatic scroll utility.
 *
 * The hook does not accept a target ref as an argument. Instead it returns a `ref` callback
 * that you attach directly to the scrollable element in JSX. This is intentional:
 *
 * A `useRef`-based approach requires a `useLayoutEffect` to sync `ref.current` into state so
 * that `useEffect` can depend on the element. But `useLayoutEffect` deps on the ref *object*,
 * not on `.current` — React's dep tracking does not observe mutations to `.current`. So if the
 * element remounts (conditional rendering, Suspense, Strict Mode double-invoke), the effect
 * deps never change and the scroll listener stays attached to the old node or never reattaches.
 *
 * A callback ref has no such indirection: React calls the function directly on every attach and
 * detach. That call becomes `setElement`, so `element` state is always the live DOM node and the
 * `useEffect` that attaches the scroll listener re-runs automatically whenever it changes.
 *
 * For cases where the scrollable element is owned by a child component and cannot have `ref`
 * attached directly in JSX, pass the `ref` callback down via props or context.
 *
 * Only `scrolled` is reported, and only when it flips. A hook that published the live scroll offset
 * would re-render its owner on every scroll event, and the owner here is `PageContainer`, which
 * rebuilds the context every page under it reads. Anything needing the offset itself should read it
 * off the element rather than have this hook push it through React.
 *
 * @param scrolledThreshold - Minimum `scrollTop` in pixels before `scrolled` flips to `true`. Defaults to `32`.
 * @returns
 *   - `scrolled` — `true` when `scrollTop > scrolledThreshold`, `false` when at the top
 *   - `scrollTo(target)` — smoothly scrolls the container to a `RefObject<HTMLElement>` or `HTMLElement`
 *   - `ref` — callback ref; attach to the scrollable element
 *
 * @example
 * ```tsx
 * const { scrolled, ref } = useScroll();
 * return <div ref={ref} className={cn('overflow-y-auto', scrolled && 'border-t')} />;
 * ```
 *
 * @example
 * ```tsx
 * // Scroll a specific message into view within the container this hook is tracking.
 * const { ref, scrollTo } = useScroll();
 * const messageRef = useRef<HTMLDivElement>(null);
 *
 * return (
 *   <div ref={ref} className="overflow-y-auto">
 *     <div ref={messageRef}>...</div>
 *     <Button onClick={() => scrollTo(messageRef)}>Jump to message</Button>
 *   </div>
 * );
 * ```
 */
export function useScroll(scrolledThreshold = 32): {
  scrolled: boolean;
  scrollTo: (target: RefObject<HTMLElement | null> | HTMLElement | null) => void;
  ref: (node: HTMLElement | null) => void;
} {
  const [element, setElement] = useState<HTMLElement | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  // A boolean, so React drops every scroll event that lands on the same side of the threshold. The
  // transcript scrolls itself once a frame while a response streams, and each of those would
  // otherwise re-render the whole page.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!element) return;

    const handleScroll = () => {
      setScrolled(element.scrollTop > scrolledThreshold);
    };

    element.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [element, scrolledThreshold]);

  const scrollTo = useCallback(
    (scrollTarget: RefObject<HTMLElement | null> | HTMLElement | null) => {
      if (!element) return;

      const targetElement = scrollTarget instanceof HTMLElement ? scrollTarget : scrollTarget?.current;
      if (!targetElement) return;

      const containerTop = element.getBoundingClientRect().top;
      const targetTop = targetElement.getBoundingClientRect().top;
      const offset = targetTop - containerTop + element.scrollTop;

      element.scrollTo({ top: offset, behavior: 'smooth' });
    },
    [element],
  );

  return { scrolled, scrollTo, ref };
}
